import './App.css';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';

import Header from './Components/Header';
import HeaderDark from './Components/HeaderDark';
import Footer from './Components/Footer';

import Home from './Pages/Home';
import About from './Pages/About';
import Services from './Pages/Services';
import Contact from './Pages/Contact';
import WebDev from './Pages/WebDev';
import DigitalMarketing from './Pages/DigitalMarketing';
import BrandStrategy from './Pages/BrandStrategy';
import DatabaseAnalysis from './Pages/DatabaseAnalysis';
import Consulting from './Pages/Consulting';
import MobileApp from './Pages/MobileApp';
import TeamMember from './Pages/TeamMember';
import Portfolio from './Pages/Portfolio';

function AppLayout() {
  const location = useLocation();
  // Only these pages use the dark sticky header
  const darkHeaderRoutes = ['/services', '/contact','/portfolio'];
  const useDarkHeader = darkHeaderRoutes.includes(location.pathname);

  const handleScheduleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    try {
      await emailjs.sendForm(
        'service_knu8zce',
        'template_0si3hgs',
        form,
        'kvPKBDlRmJBbncfYl'
      );
      alert('Your call has been scheduled successfully.');
      form.reset();
    } catch (error) {
      console.error('Schedule call email failed:', error);
      alert('Unable to send your request. Please try again.');
    }
  };

  useEffect(() => {
    // Re-initialize all vendor plugins after React mounts the DOM.
    // functions.js exposes the `e` object globally and calls e.init() on
    // DOMContentLoaded — but by that time React hasn't rendered yet, so we
    // call every initializer explicitly here instead.
    const initPlugins = () => {
      try {
        // ---- Swiper sliders ----
        if (window.Swiper) {
          document.querySelectorAll('.swiper').forEach((el) => {
            // Skip if already initialized
            if (el.swiper) return;
            let options = {
              slidesPerView: 1,
              spaceBetween: 30,
              loop: true,
            };
            try {
              const raw = el.getAttribute('data-swiper-options');
              if (raw) options = { ...options, ...JSON.parse(raw) };
            } catch (err) { /* ignore malformed JSON */ }
            new window.Swiper(el, options);
          });
        }

        // ---- Typing text (ityped) ----
        if (window.ityped) {
          document.querySelectorAll('.typed').forEach((el) => {
            if (el.dataset.itypedInit) return;
            el.dataset.itypedInit = '1';
            const strings = (el.getAttribute('data-type-text') || '').split('&&');
            const speed = parseInt(el.getAttribute('data-speed') || '200', 10);
            const backSpeed = parseInt(el.getAttribute('data-back-speed') || '50', 10);
            window.ityped.init(el, {
              strings,
              typeSpeed: speed,
              backSpeed,
              backDelay: 1200,
              startDelay: 300,
              loop: true,
              showCursor: true,
              cursorChar: '|',
            });
          });
        }

        // ---- AOS scroll animations ----
        if (window.AOS) {
          window.AOS.init({
            duration: 700,
            easing: 'ease-out-quart',
            once: true,
            offset: 60,
          });
        }

        // ---- GLightbox ----
        if (window.GLightbox) {
          window.GLightbox({
            selector: '*[data-glightbox]',
            openEffect: 'fade',
            closeEffect: 'fade',
          });
        }

        // ---- PureCounter ----
        if (window.PureCounter) {
          new window.PureCounter();
        }

        // ---- Jarallax parallax ----
        if (window.jarallax) {
          window.jarallax(document.querySelectorAll('.bg-parallax'), { speed: 0.6 });
        }

        // ---- Sticky header ----
        const stickyNav = document.querySelector('.header-sticky');
        if (stickyNav) {
          const onScroll = () => {
            if (window.pageYOffset >= 400) {
              stickyNav.classList.add('header-sticky-on');
            } else {
              stickyNav.classList.remove('header-sticky-on');
            }
          };
          window.removeEventListener('scroll', window._stickyScrollHandler);
          window._stickyScrollHandler = onScroll;
          window.addEventListener('scroll', onScroll);
        }

        // ---- Back to top ----
        const backBtn = document.querySelector('.back-top');
        if (backBtn && !backBtn.dataset.initialized) {
          backBtn.dataset.initialized = '1';
          window.addEventListener('scroll', () => {
            if (window.scrollY >= 800) {
              backBtn.classList.add('back-top-show');
            } else {
              backBtn.classList.remove('back-top-show');
            }
          });
          backBtn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
        }

        // ---- Bootstrap tooltips & popovers ----
        if (window.bootstrap) {
          document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
            if (!el._tippy) new window.bootstrap.Tooltip(el);
          });
          document.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
            new window.bootstrap.Popover(el);
          });
          // Dropdown hover (desktop only) — only activate on items that have an actual menu
          if (window.matchMedia('(min-width: 992px)').matches) {
            document.querySelectorAll('.dropdown-hover .dropdown').forEach((dd) => {
              // Skip dropdowns that have no menu element — Bootstrap crashes on null menu
              if (!dd.querySelector(':scope>.dropdown-menu')) return;

              dd.addEventListener('mouseenter', (ev) => {
                const toggle = ev.currentTarget.querySelector(':scope>[data-bs-toggle="dropdown"]');
                const menu = ev.currentTarget.querySelector(':scope>.dropdown-menu');
                if (toggle && menu && !toggle.classList.contains('show')) {
                  window.bootstrap.Dropdown.getOrCreateInstance(toggle).show();
                }
              });
              dd.addEventListener('mouseleave', (ev) => {
                const toggle = ev.currentTarget.querySelector(':scope>[data-bs-toggle="dropdown"]');
                const menu = ev.currentTarget.querySelector(':scope>.dropdown-menu');
                if (toggle && menu && toggle.classList.contains('show')) {
                  window.bootstrap.Dropdown.getOrCreateInstance(toggle).hide();
                }
              });
            });
          }
        }

        // ---- Preloader ----
        const preloader = document.querySelector('.preloader');
        if (preloader) {
          preloader.classList.add('animate__animated', 'animate__fadeOut');
          setTimeout(() => { preloader.style.display = 'none'; }, 400);
        }

      } catch (err) {
        console.warn('Plugin init error:', err);
      }
    };

    // Small delay to ensure all vendor scripts are fully ready
    const timer = setTimeout(initPlugins, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="App">
      {/* Preloader */}
      <div className="preloader">
        <div className="preloader-item">
          <div className="spinner-grow text-primary"></div>
        </div>
      </div>

      {/* Render dark header for Services & Contact, light header for all other pages */}
      {useDarkHeader ? <HeaderDark /> : <Header />}

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/webdev" element={<WebDev />} />
        <Route path="/digital-marketing" element={<DigitalMarketing />} />
        <Route path="/brand-strategy" element={<BrandStrategy />} />
        <Route path="/database-analysis" element={<DatabaseAnalysis />} />
        <Route path="/consulting" element={<Consulting />} />
        <Route path="/mobile-app" element={<MobileApp />} />
        <Route path="/team" element={<TeamMember />}/>
        <Route path="/portfolio" element={<Portfolio />}/>
      </Routes>
      <Footer />

        {/* Schedule a call offcanvas */}
        <div className="offcanvas offcanvas-end" data-bs-scroll="true" tabIndex="-1" id="scheduleCall" aria-labelledby="scheduleCallLabel">
          <div className="offcanvas-header">
            <h6 className="offcanvas-title" id="scheduleCallLabel">Schedule a call</h6>
            <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
          </div>
          <div className="offcanvas-body">
            <form className="row g-3" onSubmit={handleScheduleSubmit}>
              <div className="col-12">
                <label className="form-label">Your name *</label>
                <input type="text" name="name" className="form-control form-control-sm" placeholder="Full name" required />
              </div>
              <div className="col-12">
                <label className="form-label">Email address *</label>
                <input type="email" name="email" className="form-control form-control-sm" placeholder="name@example.com" required />
              </div>
              <div className="col-6">
                <label className="form-label">Schedule date *</label>
                <input type="date" name="date" className="form-control form-control-sm" required />
              </div>
              <div className="col-6">
                <label className="form-label">Schedule time *</label>
                <input type="time" name="time" className="form-control form-control-sm" required />
              </div>
              <div className="col-12">
                <label className="form-label">Phone number *</label>
                <input type="text" name="phone" className="form-control form-control-sm" placeholder="(xxx) xx xxxx" required />
              </div>
              <div className="col-12">
                <label className="form-label">Subject *</label>
                <input type="text" name="subject" className="form-control form-control-sm" placeholder="Subject name" required />
              </div>
              <div className="col-12">
                <label className="form-label">Message *</label>
                <textarea name="message" className="form-control" placeholder="Write your message here...." style={{ height: '150px' }} required></textarea>
              </div>
              <div className="col-12">
                <button className="btn btn-primary w-100 mb-0">Send a message</button>
              </div>
            </form>
          </div>
        </div>

        {/* Cookie alert */}
        <div className="alert alert-secondary alert-dismissible fade show position-fixed bottom-0 start-50 translate-middle-x z-index-99 rounded-3 d-none d-md-flex justify-content-between align-items-center shadow px-3 py-2" role="alert">
          <p className="heading-color fw-semibold mb-md-0">🍪 The website uses cookies to improve your web experience.</p>
          <a href="#" className="bg-success text-success bg-opacity-10 rounded-pill py-1 px-3 mb-0 ms-md-2" data-bs-dismiss="alert" aria-label="Close">Accept</a>
        </div>

        {/* Back to top */}
        <div className="back-top"></div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;
